"use client";

import MinusIcon from "public/next-public/common/icons/minus-solid.svg";
import PlusIcon from "public/next-public/common/icons/plus-solid.svg";
import React, { useEffect, useState } from "react";
import styles from "./quantityPicker.module.scss";

const QuantityPicker = ({
  value,
  min = 1,
  max = Number.MAX_SAFE_INTEGER,
  onChange,
  disabled = false,
  className,
  ariaLabel = "Quantity",
}: QuantityPickerProps) => {
  const [draft, setDraft] = useState<string>(value.toString());

  useEffect(() => {
    setDraft(value.toString());
  }, [value]);

  const clamp = (next: number): number => Math.min(Math.max(next, min), max);

  const commitDraft = (): void => {
    const parsed = parseInt(draft, 10);
    if (isNaN(parsed)) {
      setDraft(value.toString());
      return;
    }
    const next = clamp(parsed);
    setDraft(next.toString());
    if (next !== value) {
      onChange(next);
    }
  };

  const step = (delta: number): void => {
    const next = clamp(value + delta);
    if (next !== value) {
      onChange(next);
    }
  };

  return (
    <div className={[styles.QuantityPicker, className].filter(Boolean).join(" ")}>
      <button
        type="button"
        className={styles.stepButton}
        onClick={() => step(-1)}
        disabled={disabled || value <= min}
        aria-label="Decrease quantity"
      >
        <MinusIcon width={16} height={16} aria-hidden />
      </button>
      <input
        className={styles.numberField}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={draft}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(event) => setDraft(event.target.value.replace(/[^0-9]/g, ""))}
        onBlur={commitDraft}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commitDraft();
          }
        }}
      />
      <button
        type="button"
        className={styles.stepButton}
        onClick={() => step(1)}
        disabled={disabled || value >= max}
        aria-label="Increase quantity"
      >
        <PlusIcon width={16} height={16} aria-hidden />
      </button>
    </div>
  );
};

export default QuantityPicker;

export interface QuantityPickerProps {
  value: number;
  min?: number;
  max?: number;
  onChange: (next: number) => void;
  disabled?: boolean;
  className?: string;
  /** Accessible name for the quantity input. */
  ariaLabel?: string;
}
